import { createAsyncThunk } from "@reduxjs/toolkit";
import * as API from "../../services/portalService";

export const getOrders = createAsyncThunk(
  "portal/orders/list",
  async (_, { getState }) => {
    try {
      const state: any = getState();
      const {
        page,
        pageSize,
        searchQuery,
        categoryFilter,
        sortBy,
        requestType,
        dateFilter,
        subOrganizationFilter,
      } = state.portal;

      const queryParams = new URLSearchParams();

      // Pagination
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      // Search
      if (searchQuery.trim()) {
        queryParams.append("search", searchQuery.trim());
      }

      // Category/status filter (supports multi-select via comma-separated values)
      if (categoryFilter && categoryFilter !== "all") {
        const statuses = categoryFilter.split(",");
        statuses.forEach((s: string) => {
          if (s.trim()) queryParams.append("status", s.trim());
        });
      }

      if (subOrganizationFilter && subOrganizationFilter !== "all") {
        queryParams.append("subOrganization", subOrganizationFilter);
      }

      // Sorting
      switch (sortBy) {
        case "name-asc":
          queryParams.append("sort", "name");
          queryParams.append("order", "asc");
          break;
        case "name-desc":
          queryParams.append("sort", "name");
          queryParams.append("order", "desc");
          break;
        case "created-asc":
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "asc");
          break;
        case "created-desc":
        default:
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "desc");
          break;
      }

      if (requestType && requestType !== "all") {
        const types = requestType.split(",");
        types.forEach((t: string) => {
          if (t.trim()) queryParams.append("requestType", t.trim());
        });
      }

      if (dateFilter && dateFilter !== "all") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateFrom = new Date(today);
        const dateTo = new Date(today);

        switch (dateFilter) {
          case "Today":
            dateTo.setDate(dateTo.getDate() + 1);
            break;
          case "Next 7 days":
            dateTo.setDate(dateTo.getDate() + 7);
            break;
          case "Next 14 days":
            dateTo.setDate(dateTo.getDate() + 14);
            break;
          case "Next 30 days":
            dateTo.setDate(dateTo.getDate() + 30);
            break;
          default:
            break;
        }

        queryParams.append("dateFrom", dateFrom.toISOString().split("T")[0]);
        queryParams.append("dateTo", dateTo.toISOString().split("T")[0]);
      }

      const response = await API.getPortalSalesOrder(queryParams);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const getPortalReportHistory = createAsyncThunk(
  "portal/issues/history",
  async (_, { getState }) => {
    try {
      const state: any = getState();
      const {
        page,
        pageSize,
        searchQuery,
        categoryFilter,
        sortBy,
        issueType,
        subOrganizationFilter,
      } = state.portal.issues;

      const queryParams = new URLSearchParams();

      // Pagination
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      // Search
      if (searchQuery.trim()) {
        queryParams.append("search", searchQuery.trim());
      }

      // Category/status filter (supports multi-select via comma-separated values)
      if (categoryFilter && categoryFilter !== "all") {
        const statuses = categoryFilter.split(",");
        statuses.forEach((s: string) => {
          if (s.trim()) queryParams.append("status", s.trim());
        });
      }

      if (subOrganizationFilter && subOrganizationFilter !== "all") {
        queryParams.append("subOrganization", subOrganizationFilter);
      }

      // Sorting
      switch (sortBy) {
        case "name-asc":
          queryParams.append("sort", "name");
          queryParams.append("order", "asc");
          break;
        case "name-desc":
          queryParams.append("sort", "name");
          queryParams.append("order", "desc");
          break;
        case "created-asc":
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "asc");
          break;
        case "created-desc":
        default:
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "desc");
          break;
      }

      if (issueType && issueType !== "all") {
        const types = issueType.split(",");
        types.forEach((t: string) => {
          if (t.trim()) queryParams.append("issueType", t.trim());
        });
      }

      const response = await API.getPortalIssuesHistory(queryParams);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const retrieveIssueSummary = createAsyncThunk(
  "portal/issues/summary",
  async (issueId: string, { rejectWithValue }) => {
    try {
      const { data } = await API.retrieveIssueSummary(issueId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createIssueOrder = createAsyncThunk(
  "portal/issues/create/order",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.createIssueOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const patchIssueOrder = createAsyncThunk(
  "portal/issues/update/order",
  async (
    { status, issueId }: { status: string; issueId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.updateIssueOrder(issueId, status);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const shareIssueToOperation = createAsyncThunk(
  "portal/issues/share-operation",
  async ({ payload }: { payload: any }, { rejectWithValue }) => {
    try {
      const { data } = await API.shareIssueToOperation(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const manualUpdateCustomerOrderStatus = createAsyncThunk(
  "portal/customer/manual-update-order-status",
  async (
    { _id, payload }: { _id: string; payload: any },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.manualUpdateCustomerOrderStatus(_id, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
